import { ReactNode } from 'react';
import { Pill } from '@mantine/core';
import clsx from 'clsx';

import { TextWithTooltip } from '../tooltip/TextWithTooltip';
import { Tooltip } from '../tooltip/Tooltip';

import pillGroupStyles from './pill-group.module.scss';

const DEFAULT_MAX_VISIBLE_TAGS = 2;

export type SelectionPillsProps = {
  labels: readonly string[];
  maxVisibleTags?: number;
  disabled?: boolean;
};

export function SelectionPillGroup({
  children,
  fullWidth = true,
}: {
  children: ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <Pill.Group
      unstyled
      className={clsx(
        pillGroupStyles['pills-group'],
        !fullWidth && pillGroupStyles['pills-group--hug']
      )}
    >
      {children}
    </Pill.Group>
  );
}

export function SelectionPills({
  labels,
  maxVisibleTags = DEFAULT_MAX_VISIBLE_TAGS,
  disabled = false,
}: SelectionPillsProps) {
  const visibleTagLimit = Math.max(maxVisibleTags, 1);
  const visible = labels.slice(labels.length - visibleTagLimit).reverse();
  const collapsedLabels =
    labels.length > visibleTagLimit
      ? labels.slice(0, labels.length - visibleTagLimit + 1)
      : [];

  return visible.map((item, index, list) => {
    const isCounter =
      index === list.length - 1 && labels.length > visibleTagLimit;
    const text = isCounter
      ? overflowLabel(labels.length, visibleTagLimit)
      : item;

    return (
      <Pill
        unstyled
        key={`${index}-${text}`}
        withRemoveButton={false}
        className={clsx(
          pillGroupStyles['pill'],
          isCounter && pillGroupStyles['pill--overflow'],
          !isCounter && text.length > 3
            ? pillGroupStyles['pill--overflow-hidden']
            : pillGroupStyles['pill--flex-no-shrink'],
          disabled && pillGroupStyles['pill--disabled']
        )}
        data-testid={isCounter ? undefined : 'selected-pill'}
      >
        {isCounter ? (
          <Tooltip
            label={collapsedLabels.map((label, index) => (
              <span
                className={pillGroupStyles['collapsed-selection']}
                key={`${index}-${label}`}
              >
                {label}
              </span>
            ))}
            position="top"
          >
            <span className={pillGroupStyles['pill-label']}>{text}</span>
          </Tooltip>
        ) : (
          <TextWithTooltip
            textProps={{
              className: pillGroupStyles['pill-label'],
              lineClamp: 1,
              span: true,
            }}
            tooltipProps={{ position: 'top' }}
          >
            {text}
          </TextWithTooltip>
        )}
      </Pill>
    );
  });
}

function overflowLabel(total: number, maxVisibleTags: number): string {
  const hiddenCount = total - maxVisibleTags + 1;
  const prefix = maxVisibleTags > 1 ? '+' : '';
  return `${prefix} ${hiddenCount} selected`;
}
